import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { alpha } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Button } from '@/shared/components/ui';
import { GuideJourneySteps, type GuideJourneyStage } from './GuideJourneySteps';

/**
 * The one screen every guide-lifecycle state is shown on — from "application
 * received" through verification, profile building, review, approval and the
 * dead ends.
 *
 * Keeping a single presentation for all of them is deliberate: an applicant
 * returning a week later should land on the page they remember, with only the
 * words and the tracker position changed. The caller decides what the state
 * means; this component only knows how to show it.
 */

export type GuideJourneyTone = 'success' | 'waiting' | 'action' | 'error';

const TONE_ICON: Record<GuideJourneyTone, SvgIconComponent> = {
  success: CheckIcon,
  waiting: HourglassTopIcon,
  action: EditNoteIcon,
  error: PriorityHighIcon,
};

/** Palette key each tone draws its badge from. */
const TONE_PALETTE: Record<GuideJourneyTone, 'success' | 'secondary' | 'primary' | 'error'> = {
  success: 'success',
  waiting: 'secondary',
  action: 'primary',
  error: 'error',
};

/**
 * The explainer cards below the fold, keyed to the stage they describe so a
 * step already behind the applicant drops off instead of being repeated.
 */
const NEXT_STEPS = [
  { stage: 'verification', icon: ShieldOutlinedIcon },
  { stage: 'profile', icon: BadgeOutlinedIcon },
  { stage: 'review', icon: GroupsOutlinedIcon },
] as const satisfies readonly { stage: GuideJourneyStage; icon: SvgIconComponent }[];

const STAGE_ORDER: GuideJourneyStage[] = [
  'application',
  'verification',
  'profile',
  'review',
  'done',
];

/** Height the fixed navbar occupies, which the hero has to clear. */
const NAVBAR_HEIGHT = { xs: 64, md: 72 };

export interface GuideJourneyAction {
  label: string;
  onClick: () => void;
  /** Defaults to the dark fill; gold marks the one action worth taking. */
  intent?: 'primary-dark' | 'primary-gold';
}

interface GuideJourneyScreenProps {
  stage: GuideJourneyStage;
  tone?: GuideJourneyTone;
  title: string;
  body: string;
  /** Shown verbatim to the guide — must never contain internal detail. */
  note?: string;
  primaryAction?: GuideJourneyAction;
  secondaryAction?: GuideJourneyAction;
  /**
   * The hero sits beneath a transparent navbar that floats over it, so it has
   * to pad for the bar's height. False under chrome that takes its own space.
   */
  overlayNavbar?: boolean;
  /** Extra content inside the card, below the actions. */
  children?: ReactNode;
}

export function GuideJourneyScreen({
  stage,
  tone = 'waiting',
  title,
  body,
  note,
  primaryAction,
  secondaryAction,
  overlayNavbar = false,
  children,
}: GuideJourneyScreenProps) {
  const { t } = useTranslation('guideProfile');
  const Icon = TONE_ICON[tone];
  const palette = TONE_PALETTE[tone];

  // Once the journey is over — approved, suspended, verification failed —
  // "what happens next" is noise, so the explainer section goes away.
  const stageIndex = STAGE_ORDER.indexOf(stage);
  const remainingSteps =
    tone === 'error' || stage === 'done'
      ? []
      : NEXT_STEPS.filter((step) => STAGE_ORDER.indexOf(step.stage) >= stageIndex);

  return (
    <>
      <Box
        component="section"
        sx={{ position: 'relative', overflow: 'hidden', bgcolor: 'common.black' }}
      >
        {/* Same photograph as the application hero, so moving through the
            lifecycle never feels like being dropped onto a different site. */}
        <Box
          component="img"
          src="/images/guideOnboarding.png"
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: { xs: '64% center', md: 'center 38%' },
            display: 'block',
          }}
        />

        {/* Lighter than the application hero's wash: the card carries the
            contrast here, and the photograph is the reward for applying. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: {
              xs: 'linear-gradient(180deg, rgba(15,23,42,0.58) 0%, rgba(15,23,42,0.42) 100%)',
              md: 'linear-gradient(180deg, rgba(15,23,42,0.52) 0%, rgba(15,23,42,0.26) 40%, rgba(15,23,42,0.46) 100%)',
            },
          }}
        />

        <Container
          maxWidth="md"
          sx={{
            position: 'relative',
            pt: overlayNavbar
              ? {
                  xs: `calc(${NAVBAR_HEIGHT.xs}px + 28px)`,
                  md: `calc(${NAVBAR_HEIGHT.md}px + 48px)`,
                }
              : { xs: 4, md: 6 },
            pb: { xs: 5, md: 8 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              textAlign: 'center',
              borderRadius: (t) => t.radii.lg,
              bgcolor: 'background.paper',
              boxShadow: '0 24px 60px -20px rgba(15, 23, 42, 0.45)',
            }}
          >
            <Box
              sx={(theme) => ({
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2.5,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette[palette].main, 0.12),
                color: `${palette}.main`,
                border: '1px solid',
                borderColor: alpha(theme.palette[palette].main, 0.35),
              })}
            >
              <Icon sx={{ fontSize: 32 }} />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: '1.6rem', sm: '2rem' },
                letterSpacing: '-0.01em',
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.8, maxWidth: 560, mx: 'auto', mb: { xs: 4, sm: 4.5 } }}
            >
              {body}
            </Typography>

            {note && (
              <Alert
                severity="info"
                sx={{ mb: { xs: 3.5, sm: 4 }, textAlign: 'left', maxWidth: 560, mx: 'auto' }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>{t('journey.noteTitle')}</AlertTitle>
                {note}
              </Alert>
            )}

            <GuideJourneySteps
              current={stage}
              failed={tone === 'error'}
              sx={{ mb: primaryAction || secondaryAction || children ? { xs: 3.5, sm: 4.5 } : 0 }}
            />

            {(primaryAction || secondaryAction) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {primaryAction && (
                  <Button
                    intent={primaryAction.intent ?? 'primary-dark'}
                    size="large"
                    onClick={primaryAction.onClick}
                    sx={{ px: 4 }}
                  >
                    {primaryAction.label}
                  </Button>
                )}
                {secondaryAction && (
                  <Button intent="ghost" onClick={secondaryAction.onClick}>
                    {secondaryAction.label}
                  </Button>
                )}
              </Box>
            )}

            {children}
          </Paper>
        </Container>
      </Box>

      {remainingSteps.length > 0 && (
        <Box component="section" sx={{ bgcolor: 'background.default', py: { xs: 5, md: 7 } }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 6 } }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                mb: 0.75,
                fontSize: { xs: '1.3rem', md: '1.5rem' },
              }}
            >
              {t('journey.whatHappensNext')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', mb: { xs: 3.5, md: 4.5 } }}
            >
              {t('journey.stillAhead')}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: `repeat(${Math.min(remainingSteps.length, 3)}, 1fr)`,
                },
                gap: { xs: 2, md: 2.5 },
              }}
            >
              {remainingSteps.map(({ stage: stepStage, icon: StepIcon }) => (
                <Paper
                  key={stepStage}
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: (t) => t.radii.lg,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={(theme) => ({
                      width: 44,
                      height: 44,
                      mb: 2,
                      borderRadius: (t) => t.radii.md,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                    })}
                  >
                    <StepIcon fontSize="small" />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.75 }}>
                    {t(`journey.nextSteps.${stepStage}.title`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {t(`journey.nextSteps.${stepStage}.body`)}
                  </Typography>
                </Paper>
              ))}
            </Box>

            <Paper
              elevation={0}
              sx={(theme) => ({
                mt: { xs: 2.5, md: 3 },
                p: { xs: 2.5, md: 3 },
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                borderRadius: (t) => t.radii.lg,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.14),
              })}
            >
              <LockOutlinedIcon sx={{ color: 'primary.main', mt: 0.25 }} fontSize="small" />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                  {t('journey.privacy.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {t('journey.privacy.body')}
                </Typography>
              </Box>
            </Paper>
          </Container>
        </Box>
      )}
    </>
  );
}
